import { doc, getDoc } from 'firebase/firestore';

import { db } from '@/firebase-config';
import { buildProjectDocPath, type ProjectScope } from '@/features/sync/project-scope';
import type { ProjectBudget } from '@/types/shared';
import type { ProjectBudgetClient } from '../contracts';
import { isRecord, readNumber, readString } from './firestore-read-normalizers';

type BudgetSnapshot = {
  exists: () => boolean;
  data: () => unknown;
};

type BudgetCategory = NonNullable<ProjectBudget['costumeCategories']>[number];

const BUDGETS_COLLECTION = 'budgets';
const CURRENT_BUDGET_DOC = 'current';
const DEFAULT_CURRENCY = 'USD';

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function budgetCategories(value: unknown): BudgetCategory[] | undefined {
  if (!Array.isArray(value)) return;

  return value.flatMap((item) => {
    if (!isRecord(item) || typeof item.name !== 'string' || typeof item.allocatedAmount !== 'number') {
      return [];
    }

    return [{
      name: item.name,
      allocatedAmount: item.allocatedAmount,
    }];
  });
}

function toProjectBudget(scope: ProjectScope, value: unknown): ProjectBudget {
  const data = isRecord(value) ? value : {};

  return {
    id: readString(data.id, CURRENT_BUDGET_DOC),
    projectId: readString(data.projectId, scope.projectId),
    costumesTotalBudget: readNumber(data.costumesTotalBudget),
    propsTotalBudget: readNumber(data.propsTotalBudget),
    costumeCategories: budgetCategories(data.costumeCategories),
    propsCategories: budgetCategories(data.propsCategories),
    currency: readString(data.currency, DEFAULT_CURRENCY),
    fiscalYear: optionalString(data.fiscalYear),
    notes: optionalString(data.notes),
    createdBy: readString(data.createdBy),
    createdAt: readNumber(data.createdAt),
    lastUpdatedBy: readString(data.lastUpdatedBy),
    lastUpdatedAt: readNumber(data.lastUpdatedAt),
  };
}

export class FirebaseProjectBudgetClient implements ProjectBudgetClient {
  async getProjectBudget(scope: ProjectScope): Promise<ProjectBudget | null> {
    const snapshot = await getDoc(
      doc(db, buildProjectDocPath(scope), BUDGETS_COLLECTION, CURRENT_BUDGET_DOC),
    ) as BudgetSnapshot;

    return snapshot.exists() ? toProjectBudget(scope, snapshot.data()) : null;
  }
}
